import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { Vector3 } from "three";
import { useThree } from "@react-three/fiber";

import { useServicesState } from "../../store/servicesStateStore";
import { useCurrentCommandState } from "../../store/currentCommandStateStore";
import { useProductModelStore } from "../../store/productModelStore";
import { findFromProducts } from "../../utils/productModelUtils";
import { ElementTypeEnum } from "../../elementTypeMappings";
import { DebugCrosshairComponent } from "../debug-components/DebugCrosshairComponent";
import { findProductsFromSceneByGlobalIds } from "../../utils/geometryUtils";
import { useStoreyVisibilityStore } from "../../store/storeyVisibilityStore";
import { isFullyVisible } from "../../utils/sceneObjectUtils";
import RoomNoteClickListener from "../../inputListeners/RoomNoteClickListener";
import RoomNoteHoverListener from "../../inputListeners/RoomNoteHoverListener";

const InputListenerComponent = () => {
    const {gl, camera, scene, size} = useThree();
    const {canvasInputListener} = useServicesState();
    const {products} = useProductModelStore();
    const { hiddenStoreyGlobalIds } = useStoreyVisibilityStore();
    
    const canvasCursor = useCurrentCommandState(state => state.canvasCursor);
    const [debugHitPoint, setDebugHitPoint] = useState<Vector3 | null>(null);
    const allTargetObjectsRef = useRef<THREE.Object3D[]>([]);

    canvasInputListener.setScene(scene);

    useEffect(() => {
        if (products.length > 0) {
            const targetProducts = findFromProducts(products, Object.values(ElementTypeEnum));
            allTargetObjectsRef.current = findProductsFromSceneByGlobalIds(scene, targetProducts.map(p => p.globalId));
        }
        else {
            allTargetObjectsRef.current = [];
        }
        canvasInputListener.setDefaultTargetObjects(allTargetObjectsRef.current.filter(isFullyVisible));
        canvasInputListener.resetTargetObjects();
    }, [products]);

    useEffect(() => {
        canvasInputListener.setDefaultTargetObjects(allTargetObjectsRef.current.filter(isFullyVisible));
        canvasInputListener.resetTargetObjects();
    }, [hiddenStoreyGlobalIds]);
 
    useEffect(() => {
        canvasInputListener.setCamera(camera);
    }, [camera]);

    useEffect(() => {
        const roomNoteClickListener = new RoomNoteClickListener();
        const roomNoteHoverListener = new RoomNoteHoverListener();
        canvasInputListener.addDefaultClickListener(roomNoteClickListener);
        canvasInputListener.addDefaultHoverListener(roomNoteHoverListener);
        return () => {
            canvasInputListener.removeDefaultClickListener(roomNoteClickListener);
            canvasInputListener.removeDefaultHoverListener(roomNoteHoverListener);
        };
    }, [canvasInputListener]);

    useEffect(() => {
        canvasInputListener.windowSize = { width: size.width, height: size.height, top: size.top, left: size.left };
    }, [size]);

    useEffect(() => {
        gl.domElement.style.cursor = canvasCursor;
    }, [canvasCursor, gl.domElement]);

    useEffect(() => {

        const canvas = gl.domElement;
          
        canvasInputListener.attachToCanvas(canvas);

        return () => { 
            canvasInputListener.detachFromCanvas(canvas);
        };
    }, [gl])

    return <>
        <DebugCrosshairComponent position={debugHitPoint}/>
        
    </>
};

export default InputListenerComponent;
